import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Handshake, Menu, Users, X } from 'lucide-react';
import { Logo } from '../Logo';
import { UserMenu } from '../UserMenu';
import { useIsMobile } from '../../hooks/useIsMobile';

export type AdminToolId = 'users';

interface AdminToolDef {
  id: AdminToolId;
  slug: string;
  label: string;
  icon: React.ReactNode;
}

export const ADMIN_TOOLS: AdminToolDef[] = [
  { id: 'users', slug: 'users', label: 'Users', icon: <Users className="h-5 w-5" /> },
];

export const DEFAULT_ADMIN_TOOL_ID: AdminToolId = 'users';

export function getAdminToolSlug(id: AdminToolId): string {
  return ADMIN_TOOLS.find((tool) => tool.id === id)?.slug ?? id;
}

export function getAdminToolIdFromSlug(slug: string): AdminToolId | null {
  return ADMIN_TOOLS.find((tool) => tool.slug === slug)?.id ?? null;
}

export function adminToolPath(id: AdminToolId): string {
  return `/admin/${getAdminToolSlug(id)}`;
}

export type AdminLayoutContentRenderer = (ctx: {
  mobileNavTrigger: React.ReactNode;
}) => React.ReactNode;

interface AdminLayoutProps {
  children: React.ReactNode | AdminLayoutContentRenderer;
  activeId: AdminToolId;
  onSelectTool: (id: AdminToolId) => void;
  backLabel: string;
  onBack: () => void;
  badge?: string;
}

export function AdminLayout({
  children,
  activeId,
  onSelectTool,
  backLabel,
  onBack,
  badge = 'Admin',
}: AdminLayoutProps) {
  const isMobile = useIsMobile();
  const [navOpen, setNavOpen] = useState(false);
  const renderContent = (mobileNavTrigger: React.ReactNode) =>
    typeof children === 'function'
      ? (children as AdminLayoutContentRenderer)({ mobileNavTrigger })
      : children;

  if (isMobile) {
    const mobileNavTrigger = (
      <button
        type="button"
        aria-label="Open admin tools"
        onClick={() => setNavOpen(true)}
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-900"
      >
        <Menu className="h-5 w-5" />
      </button>
    );

    return (
      <div className="flex min-h-screen flex-col bg-gray-50">
        <AdminHeader badge={badge} backLabel={backLabel} onBack={onBack} compact />
        <main className="flex-1 px-4 py-5">{renderContent(mobileNavTrigger)}</main>
        <AdminNavSheet
          open={navOpen}
          activeId={activeId}
          onClose={() => setNavOpen(false)}
          onSelect={(id) => {
            setNavOpen(false);
            onSelectTool(id);
          }}
        />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      <AdminHeader badge={badge} backLabel={backLabel} onBack={onBack} />
      <div className="flex flex-1">
        <aside className="w-60 border-r border-gray-200 bg-white">
          <AdminNav activeId={activeId} onSelect={onSelectTool} />
        </aside>
        <main className="flex-1 p-8">{renderContent(null)}</main>
      </div>
    </div>
  );
}

interface AdminHeaderProps {
  badge: string;
  backLabel: string;
  onBack: () => void;
  compact?: boolean;
}

export function AdminHeader({ badge, backLabel, onBack, compact = false }: AdminHeaderProps) {
  return (
    <header className="border-b border-gray-200 bg-white">
      <div
        className={`${compact ? 'px-4 py-3' : 'max-w-full px-4 py-4 sm:px-6 lg:px-8'} flex items-center justify-between gap-3`}
      >
        <div className="flex min-w-0 items-center gap-2">
          <Handshake className="h-7 w-7 shrink-0 text-primary" />
          <Logo className="truncate text-xl font-bold text-gray-900" />
          <span className="ml-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
            {badge}
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className={compact ? 'sr-only' : undefined}>{backLabel}</span>
          </button>
          <UserMenu />
        </div>
      </div>
    </header>
  );
}

function AdminNav({
  activeId,
  onSelect,
}: {
  activeId: AdminToolId;
  onSelect: (id: AdminToolId) => void;
}) {
  return (
    <nav className="space-y-1 p-3">
      {ADMIN_TOOLS.map((tool) => {
        const active = activeId === tool.id;
        return (
          <button
            key={tool.id}
            type="button"
            onClick={() => onSelect(tool.id)}
            className={`flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
              active ? 'bg-primary/10 text-primary' : 'text-gray-700 hover:bg-gray-50'
            }`}
          >
            {tool.icon}
            {tool.label}
          </button>
        );
      })}
    </nav>
  );
}

interface AdminNavSheetProps {
  open: boolean;
  activeId: AdminToolId;
  onClose: () => void;
  onSelect: (id: AdminToolId) => void;
}

function AdminNavSheet({ open, activeId, onClose, onSelect }: AdminNavSheetProps) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label="Admin tools">
      <button
        type="button"
        aria-label="Close admin tools"
        onClick={onClose}
        className="absolute inset-0 h-full w-full bg-gray-900/40"
      />
      <div className="absolute bottom-0 left-0 right-0 rounded-t-xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
          <div className="text-sm font-semibold text-gray-900">Admin tools</div>
          <button
            type="button"
            aria-label="Close admin tools"
            onClick={onClose}
            className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <AdminNav activeId={activeId} onSelect={onSelect} />
      </div>
    </div>
  );
}

export function useAdminNavigate() {
  const navigate = useNavigate();
  return {
    openTool: (id: AdminToolId) => navigate(adminToolPath(id)),
    backToApp: () => navigate('/app'),
  };
}
